import { FC, ReactNode, useRef, useEffect, useState, useCallback, Fragment, useMemo } from 'react';
import { Box, IconButton, List, Paper, useMediaQuery, useTheme } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

import { MessageFeedbackView } from '@cvt/components/MessageFeedbackView';
import { usePagination } from '@cvt/hooks/usePagination';
import { useUniversities } from '@modules/Universities/hooks/useUniversities';

interface ViewProps {
  filters?: Universities.GetListParams;
  renderCardItem: (university: Universities.University) => ReactNode;
  renderListItem: (university: Universities.University) => ReactNode;
  renderSkeleton: () => React.ReactNode;
}

const PAGE_SIZE = 10;
const MAX_PARTNER_CARDS = 5;

export const PaginatedUniversityList: FC<ViewProps> = ({
  filters = {},
  renderCardItem,
  renderListItem,
  renderSkeleton,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { page, setPage, totalPages, setCount } = usePagination(PAGE_SIZE);

  const { limit, offset } = useMemo(() => {
    return { limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE };
  }, [page]);

  const {
    universities: currentPageUniversities,
    count,
    isFetching,
  } = useUniversities({ ...filters, limit, offset });

  const {
    universities: partnersByMatch = [],
  } = useUniversities({
    ...filters,
    limit: MAX_PARTNER_CARDS,
    offset: 0,
    sponsored: true,
    matchGt: 0,
  });

  const {
    universities: partnersFallback = [],
  } = useUniversities({
    ...filters,
    limit: MAX_PARTNER_CARDS,
    offset: 0,
    sponsored: true,
  });

  const partnerUniversities = useMemo(
    () => (partnersByMatch.length > 0 ? partnersByMatch : partnersFallback),
    [partnersByMatch, partnersFallback],
  );

  useEffect(() => {
    setCount(count);
  }, [count, setCount]);

  const _renderSkeleton = useCallback(() => {
    if (!isFetching) return null;
    return Array.from({ length: 3 }).map((_, idx) => <Fragment key={idx}>{renderSkeleton()}</Fragment>);
  }, [isFetching, renderSkeleton]);

  const [currentIndex, setCurrentIndex] = useState(0);

  const getActualCardWidth = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return 0;
    const calculatedWidth = container.offsetWidth * 0.85;
    const maxWidth = 280;
    return Math.min(calculatedWidth, maxWidth);
  }, []);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const cardWidth = getActualCardWidth();
    if (!cardWidth) return;
    const newIndex = Math.round(container.scrollLeft / cardWidth);
    if (newIndex !== currentIndex) setCurrentIndex(newIndex);
  }, [currentIndex, getActualCardWidth]);

  const scrollToIndex = useCallback((index: number) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const cardWidth = getActualCardWidth();
    container.scrollTo({ left: index * cardWidth, behavior: 'smooth' });
  }, [getActualCardWidth]);

  const handlePreviousCard = useCallback(() => {
    const newIndex = Math.max(0, currentIndex - 1);
    scrollToIndex(newIndex);
    setCurrentIndex(newIndex);
  }, [currentIndex, scrollToIndex]);

  const handleNextCard = useCallback(() => {
    const newIndex = Math.min((partnerUniversities?.length ?? 1) - 1, currentIndex + 1);
    scrollToIndex(newIndex);
    setCurrentIndex(newIndex);
  }, [currentIndex, partnerUniversities?.length, scrollToIndex]);

  useEffect(() => setCurrentIndex(0), [partnerUniversities]);

  const hasPrevPage = page > 1;
  const hasNextPage = page < totalPages;

  if (count === 0) {
    return <MessageFeedbackView message="No universities" height="100%" />;
  }

  return (
    <Fragment>
      {currentPageUniversities.length > 0 && (
        <Box px={2}>
          <Paper variant="elevation" elevation={1} sx={{ overflow: 'hidden' }}>
            <List disablePadding>
              {currentPageUniversities.map(university => (
                <Fragment key={university.id}>{renderListItem(university)}</Fragment>
              ))}
            </List>
            {_renderSkeleton()}
          </Paper>

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" alignItems="center" gap={1} mt={1}>
              <IconButton
                onClick={() => setPage(page - 1)}
                disabled={!hasPrevPage}
                aria-label="Previous page"
                size="large"
              >
                <ChevronLeft />
              </IconButton>
              <IconButton
                onClick={() => setPage(page + 1)}
                disabled={!hasNextPage}
                aria-label="Next page"
                size="large"
              >
                <ChevronRight />
              </IconButton>
            </Box>
          )}
        </Box>
      )}

      {partnerUniversities.length > 0 && (
        <Box position="relative" width="100%" mt={2}>
          <Box
            ref={scrollContainerRef}
            onScroll={handleScroll}
            sx={{
              display: 'flex',
              overflowX: 'auto',
              scrollSnapType: 'x mandatory',
              scrollBehavior: 'smooth',
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              px: 2,
              boxSizing: 'border-box',
              ...(!isMobile && ({
                '-webkit-mask-image':
                          'linear-gradient(to right, transparent 0% , rgba(0,0,0,1) 16px, rgba(0,0,0,1) calc(100% - 16px), transparent 100%)',
                '-webkit-mask-repeat': 'no-repeat',
              })),
            }}
          >
            {partnerUniversities.map((university) => (
              <Box
                key={university.id}
                sx={{
                  flexShrink: 0,
                  width: '85%',
                  maxWidth: 280,
                  scrollSnapAlign: 'center',
                  pr: 2,
                  '&:first-of-type': { pl: 0 },
                  '&:last-of-type': { pr: 0 },
                }}
              >
                {renderCardItem(university)}
              </Box>
            ))}
          </Box>

          {!isMobile && partnerUniversities.length > 1 && (
            <Fragment>
              <IconButton
                onClick={handlePreviousCard}
                disabled={currentIndex <= 0}
                sx={{
                  position: 'absolute',
                  left: -60,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'background.paper',
                  boxShadow: 2,
                  zIndex: 1,
                }}
                size="large"
              >
                <ChevronLeft />
              </IconButton>

              <IconButton
                onClick={handleNextCard}
                disabled={currentIndex >= partnerUniversities.length - 1}
                sx={{
                  position: 'absolute',
                  right: -60,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  backgroundColor: 'background.paper',
                  boxShadow: 2,
                  zIndex: 1,
                }}
                size="large"
              >
                <ChevronRight />
              </IconButton>
            </Fragment>
          )}
        </Box>
      )}
    </Fragment>
  );
};